import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Spin, Switch, Tag } from "antd";
import { ago, errorText } from "./format";

// The Automation page's status and controls inside the Mini App: automation
// on/off, IB Gateway start/stop/restart, health and logs. Same rules as
// components/Automation: the container answering (gateway status over its
// socket) = AutoTrader online; its trading loop checking in (heartbeat) = not
// stuck. Health only with the bot's stats (older versions send none); logs only
// on demand. The IBKR login stays on the website.
const REFRESH_MS = 15 * 1000;
const LOOP_SILENT_SECONDS = 30;
const STATS_FRESH_MS = 2 * 60 * 1000;
const LOG_LINES = 200;

const ms = (value) =>
  value == null ? "-" : value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${Math.round(value)}ms`;

const AutomationTab = ({ api, webApp }) => {
  const [overview, setOverview] = useState(null);
  // null = not asked yet; { reachable, running }
  const [gateway, setGateway] = useState(null);
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(null);
  const [logs, setLogs] = useState(null); // null = logs closed
  const [now, setNow] = useState(Date.now());

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/api/miniapp/automation");
      setOverview(data);
      return data;
    } catch (err) {
      return null;
    }
  }, [api]);

  const loadBot = useCallback(async () => {
    try {
      const { data } = await api.get("/api/miniapp/automation/gateway");
      setGateway({ reachable: true, running: !!data });
    } catch (err) {
      setGateway({ reachable: false, running: false });
      setAccount(null);
      return;
    }
    try {
      const { data } = await api.get("/api/miniapp/automation/account");
      setAccount(data);
    } catch (err) {
      setAccount(null);
    }
  }, [api]);

  const refresh = useCallback(async () => {
    setNow(Date.now());
    const data = await loadOverview();
    if (data?.enabled) loadBot();
  }, [loadOverview, loadBot]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await api.get("/api/miniapp/automation/logs");
      setLogs(String(data || "").split("\n").reverse().slice(0, LOG_LINES).join("\n"));
    } catch (err) {
      setLogs(errorText(err, "AutoTrader didn't answer"));
    }
  }, [api]);

  // logs only on demand: Show / Refresh (each asks the bot over its socket)
  const logsOpen = logs !== null;

  // confirm in Telegram, run, tell the result, refresh (the gateway takes a
  // moment to start / stop)
  const run = (key, question, path, done) => {
    const go = async () => {
      setBusy(key);
      try {
        await api.post(path);
        webApp.HapticFeedback?.notificationOccurred("success");
        if (done) webApp.showAlert(done);
      } catch (err) {
        webApp.HapticFeedback?.notificationOccurred("error");
        webApp.showAlert(errorText(err));
      } finally {
        setBusy(null);
        setTimeout(refresh, key === "automation" ? 0 : 3000);
      }
    };
    if (question) webApp.showConfirm(question, (ok) => ok && go());
    else go();
  };

  if (!overview) {
    return (
      <div className="mini-app-center">
        <Spin />
      </div>
    );
  }
  if (!overview.enabled) {
    return (
      <p className="mini-app-empty">
        Automation isn't enabled for your account. Ask the admin to enable it.
      </p>
    );
  }

  const stats = overview.stats || {};
  const legacyBot = !overview.stats; // older versions send no stats / heartbeat
  const checked = gateway !== null;
  const online = !!gateway?.reachable;
  const loopStuck =
    online &&
    !legacyBot &&
    (overview.seenSecondsAgo == null || overview.seenSecondsAgo > LOOP_SILENT_SECONDS);
  const statsFresh =
    !!overview.statsAt && dayjs().diff(dayjs(overview.statsAt)) < STATS_FRESH_MS;
  const ibConnected = statsFresh ? stats.ibConnected : account?.connected;
  const loginMissing = online && account && account.ibcStatus !== "configured";

  const status = !checked
    ? { color: "default", text: "Checking…" }
    : !online
      ? { color: "red", text: "Offline" }
      : !overview.running
        ? { color: "default", text: "Off" }
        : loopStuck
          ? { color: "red", text: "Stuck" }
          : ibConnected === false
            ? { color: "orange", text: "Not connected to IBKR" }
            : { color: "green", text: "Running" };

  return (
    <>
      <div className="mini-app-header">
        <div>
          <div className="mini-app-title">Automation</div>
          <div className="mini-app-sub">
            {stats.version ? `AutoTrader ${stats.version}` : "AutoTrader"}
          </div>
        </div>
        <Tag color={status.color} className="mini-app-status-tag">
          {status.text}
        </Tag>
      </div>

      <div className="mini-app-card">
        <div className="mini-app-row mini-app-control">
          <div>
            <div className="mini-app-symbol">Automation</div>
            <div className="mini-app-sub">
              AutoTrader places and manages orders only while it's on
            </div>
          </div>
          <Switch
            checked={overview.running}
            loading={busy === "automation"}
            onChange={(on) =>
              run(
                "automation",
                on
                  ? "Turn automation on? AutoTrader will place your strategies' orders."
                  : "Turn automation off? AutoTrader stops placing and managing orders.",
                on ? "/api/miniapp/automation/start" : "/api/miniapp/automation/stop",
              )
            }
          />
        </div>
      </div>

      <div className="mini-app-card">
        <div className="mini-app-row">
          <div>
            <div className="mini-app-symbol">IB Gateway</div>
            <div className="mini-app-sub">
              {!checked
                ? "Checking…"
                : !online
                  ? "AutoTrader isn't responding"
                  : gateway.running
                    ? "Running"
                    : "Stopped"}
            </div>
          </div>
          {online && (
            <div className="mini-app-buttons">
              {gateway.running ? (
                <>
                  <Button
                    size="small"
                    loading={busy === "restart"}
                    onClick={() =>
                      run(
                        "restart",
                        "Restart the IB Gateway? You may need to approve the login in the IBKR app.",
                        "/api/miniapp/automation/gateway/restart",
                        "IB Gateway is restarting",
                      )
                    }
                  >
                    Restart
                  </Button>
                  <Button
                    size="small"
                    danger
                    loading={busy === "stop"}
                    onClick={() =>
                      run(
                        "stop",
                        "Stop the IB Gateway? AutoTrader can't trade until it's started again.",
                        "/api/miniapp/automation/gateway/stop",
                        "IB Gateway stopped",
                      )
                    }
                  >
                    Stop
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  loading={busy === "start"}
                  onClick={() =>
                    run(
                      "start",
                      null,
                      "/api/miniapp/automation/gateway/start",
                      "IB Gateway is starting. Approve the login in the IBKR app if asked.",
                    )
                  }
                >
                  Start
                </Button>
              )}
            </div>
          )}
        </div>
        {loginMissing && (
          <p className="mini-app-note">
            No IBKR login yet: set it on the dindicator website (Automation page).
          </p>
        )}
      </div>

      {/* older AutoTrader versions send no stats: no Health card for them */}
      {!legacyBot && (
        <div className="mini-app-card">
          <div className="mini-app-symbol">Health</div>
          <div className="mini-app-health">
            <span>AutoTrader</span>
            <span className={!checked ? "" : online && !loopStuck ? "green" : "red"}>
              {!checked ? "-" : !online ? "Offline" : loopStuck ? "Stuck" : "Online"}
            </span>
            <span>IBKR</span>
            <span className={ibConnected ? "green" : ibConnected === false ? "red" : ""}>
              {ibConnected == null ? "-" : ibConnected ? "Connected" : "Disconnected"}
            </span>
            <span>Loop time</span>
            <span>
              {ms(stats.loopAvgMs)} <small>max {ms(stats.loopMaxMs)}</small>
            </span>
            <span>Working orders</span>
            <span>{stats.workingOrders ?? "-"}</span>
            <span>Open positions</span>
            <span>
              {stats.stockPositions == null
                ? "-"
                : stats.stockPositions + (stats.optionPositions || 0)}
            </span>
            <span>Updated</span>
            <span>{ago(overview.statsAt, now)}</span>
          </div>
          {stats.lastError && (
            <p className="mini-app-note">
              Last error {ago(stats.lastErrorAt, now)}:{" "}
              <span className="red">{stats.lastError}</span>
            </p>
          )}
        </div>
      )}

      <div className="mini-app-card">
        <div className="mini-app-row">
          <div className="mini-app-symbol">Logs</div>
          <div className="mini-app-buttons">
            {logsOpen && (
              <Button size="small" onClick={loadLogs}>
                Refresh
              </Button>
            )}
            <Button
              size="small"
              disabled={!online}
              onClick={() => {
                if (logsOpen) setLogs(null);
                else {
                  setLogs("Loading…");
                  loadLogs();
                }
              }}
            >
              {logsOpen ? "Hide" : "Show"}
            </Button>
          </div>
        </div>
        {logsOpen && <pre className="mini-app-logs">{logs || "No logs"}</pre>}
      </div>
    </>
  );
};

export default AutomationTab;
